import { ISelectorProps, Selector } from "@/components/Selector"
import { cn, moneyFormat } from "@/utils"
import { ICard, maskCardNumber } from "@usher/pe-client-front-end-lib"
import { ReactNode, useMemo } from "react"
import { CardStatus } from "../../View/Details/CardStatus"

interface SelectProps extends Omit<ISelectorProps<string, { balance?: string; currency?: string }>, "options" | "getValueLabel"> {
  cards: ICard[]
  cardId?: number
  withAllOption?: boolean
}

export const CardSelect = ({ withAllOption, cardId, cards, ...props }: SelectProps) => {
  const cardsOptions = useMemo(() => {
    const options =
      cards?.map((card) => ({
        label: maskCardNumber(card.cardPan || card.card_pan),
        value: card.id,
        meta: {
          balance: card.balance,
          currency: card.card_currency || card.currency,
          status: card.status,
        },
      })) ?? []
    // @ts-ignore
    if (withAllOption) options.unshift({ label: "All", value: "All" })
    return options
  }, [cards, withAllOption])

  const selectValue = props.value

  const value = useMemo(() => {
    if (typeof selectValue === "object") return cardsOptions.find((option) => option.value === selectValue.value)
    if ((typeof selectValue === "string" && Number(selectValue) > 0) || selectValue === "All")
      return cardsOptions.find((option) => option.value === selectValue)
  }, [selectValue, cardsOptions])

  return (
    <Selector
      variant="contained"
      fullWidth
      {...props}
      value={value}
      options={cardsOptions}
      // @ts-ignore
      renderOptions={(option, props, key) => {
        const balance = Number(option?.meta?.balance ?? 0)
        if (option.value === "All")
          return (
            <div {...props} key={key}>
              All
            </div>
          )
        const status = option.meta.status
        return (
          <div {...props} key={key} className={cn("flex gap-2 items-center", props.className)}>
            <span className="">
              {option?.label} (Balance:{" "}
              <span
                className={cn({
                  "text-red-600": Number(balance) < 0,
                  "text-green-600": Number(balance) > 0,
                })}
              >
                {moneyFormat(Number(balance), 2)} {option?.meta?.currency}
              </span>
              )
            </span>
            {status && <CardStatus status={status} className="h-6 px-2" />}
          </div>
        ) as ReactNode
      }}
      getValueLabel={(option) => {
        if (option == null) return ""
        if (typeof option === "string") return option
        if (option.value === "All") return option.label
        const status = option.meta?.status
        const balance = option?.meta?.balance ?? "0"
        return (
          <div className="inline-flex items-center gap-2 text-nowrap flex-wrap">
            <span>
              {option?.label} (Balance:{" "}
              <span
                className={cn({
                  "text-red-600": Number(balance) < 0,
                  "text-green-600": Number(balance) > 0,
                })}
              >
                {moneyFormat(Number(balance), 2)} {option?.meta?.currency}
              </span>
              {") "}
            </span>
            {status && <CardStatus status={status} className="h-6 px-2" />}
          </div>
        )
      }}
    />
  )
}
